import styled from 'styled-components';
import { medias } from '@/utils/sizes';
import { Typography } from '@/components/atoms/Typography/Typography.styled';

export const ListSubfilters = styled.ul`
  padding-left: var(--spacing-component-inner-sm);
  display: flex;
  flex-direction: column;
  gap: 1px;

  ${medias.only.mobile} {
    padding-left: 0;
    gap: 0;
  }
`;

export const LocationSubfilters = styled.div`
  grid-column-start: 3;
`

export const FiltersSubSection = styled.li`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-component-inner-xs);

  &:has(${ListSubfilters}) {
    margin-bottom: var(--spacing-component-inner);
  }

  &:not(:has(${ListSubfilters})) {
    margin-bottom: 5px;
  }

  &.filters-subsection:not(:has(button)):has(+ .filters-subsection) {
    margin-bottom: var(--spacing-component-inner);
  }

  &.filters-subsection {
    margin-bottom: var(--spacing-component-inner);

    li[data-disabled='true'] {
      position: relative;
      pointer-events: none;

      &:before {
        content: '';
        position: absolute;
        top: 50%;
        left: -10px;
        transform: translateY(-50%);
        width: 4px;
        height: 4px;
        border-radius: 10px;
        background-color: var(--c-font-primary);
        opacity: 1;
        transition: opacity var(--tr-base);
      }
    }
  }

  &:last-child {
    margin-bottom: 0;
  }

  ${medias.only.mobile} {
    gap: 0;
  }
`;

export const Column = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isDisplayed'].includes(prop),
})<{
  isDisplayed: boolean;
}>`
  display: grid;
  grid-template-columns: 37% 63%;
  opacity: ${({ isDisplayed }) => (isDisplayed ? 1 : 0)};

  ${Typography}.filters-count {
    display: none;
  }

  ${medias.only.mobile} {
    grid-template-columns: 100%;

    ${Typography}.filters-count {
      display: inline-flex;
      margin-left: 3px;
      margin-bottom: 3px;
      font-size: var(--fs-lg);
      transform: translateY(-4px);
    }
  }

  button.btn-collapsible-mobile {
    pointer-events: none;
  }

  .filters-subsection:has(button.selected) {    
    > li {
      position: relative;

      .typography {
        color: var(--c-font-primary);
      }
    }

    .filter-item:before,
    button.selected:before {
      content: '';
      position: absolute;
      top: 50%;
      left: -10px;
      transform: translateY(-50%);
      width: 4px;
      height: 4px;
      border-radius: 10px;
      background-color: var(--c-font-primary);
      opacity: 1;
      transition: opacity var(--tr-base);
    }
  }

  ${medias.only.mobile} {
    opacity: 1;
    button.btn-collapsible-mobile {
      pointer-events: all;
      display: flex;
      align-items: center;
      flex-direction: row-reverse;
      justify-content: space-between;
      height: 50px;
      border-bottom: 1px solid var(--c-border);
      padding: 0 var(--page-pd-mobile-tablet) 0
        calc(var(--page-pd-mobile-tablet) * 2);

      span {
        display: flex;
        font-size: var(--fs-xl);
      }
    }
  }

  ${medias.min.tablet} {
    &.location-section {
      grid-template-columns: repeat(3, 33%);

      > ul {
        /* display: grid;
        grid-template-columns: 50% 50%; */
        grid-row-start: 1;
        grid-column-start: 2;
        grid-column-end: 4;

        li {
          grid-column-start: 1;
        }
      }

      ${LocationSubfilters} {
        grid-row-start: 1;
        grid-column-start: 3;
      }

      ${FiltersSubSection} {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-column-start: 2;
        grid-column-end: 4;
      }
    }
  }
`;

export const FilterItem = styled.li.withConfig({
  shouldForwardProp: (prop) => !['hasMarginBottom'].includes(prop),
})<{
  hasMarginBottom?: boolean;
}>`
  all: unset;
  display: flex;
  align-items: center;
  margin-bottom: 0;

  &:has(+ .filters-subsection) {
    margin-bottom: var(--spacing-component-inner);
  }

  ${medias.only.mobile} {
    p:not(.filters-count) {
      font-size: var(--fs-lg);
      color: var(--c-font-primary);
    }

    &:has(p) {
      min-height: 35px;
    }
  }

  &[data-disabled='true'] {
    pointer-events: none;

    ${Typography} {
      color: var(--c-font-disabled);
    }

    button:before {
      opacity: 1;
    }
  }
`;

export const FiltersSection = styled.ul`
  display: flex;
  flex-direction: column;

  button.location-filter-opened:before {
    opacity: 1;
    background-color: var(--c-font-secondary);
  }

  .location-subfilter-selected {
    .typography {
      color: var(--c-font-primary);
    }

    &:before {
      opacity: 1 !important;
      background-color: var(--c-font-primary) !important;
    }
  }

  ${medias.only.mobile} {
    position: relative;
    height: 0;
    overflow: hidden;
    padding: 0 var(--page-pd-mobile-tablet) 0
      calc(var(--page-pd-mobile-tablet) * 2);
    transition: height var(--tr-base);
    overflow: hidden;

    &:after {
      content: '';
      position: absolute;
      display: block;
      width: 100%;
      height: 1px;
      bottom: 0;
      left: 0;
      opacity: 0;
      background-color: var(--c-border);
      transition: opacity var(--tr-base);
    }

    ${FilterItem} button {
      min-height: 39px;

      span {
        font-size: var(--fs-xl);
      }
    }

    > ${FilterItem}:first-child {
      padding-top: var(--page-pd-mobile-tablet);
    }

    > ${FilterItem}:last-child {
      padding-bottom: var(--page-pd-mobile-tablet);
    }

    ${FiltersSubSection} {
      opacity: 0;
      transition: opacity 0.15s 0.15s;

      &:first-child {
        padding-top: var(--page-pd-mobile-tablet);
      }

      &:last-child {
        padding-bottom: var(--page-pd-mobile-tablet);
      }

      > ${FilterItem} {
        // en mobile el titulo de sección de filtros no es seleccionable:
        cursor: default;
        pointer-events: none;

        span {
          color: var(--c-font-primary);
          font-size: var(--fs-lg);
        }
      }
    }

    ${medias.only.mobile} {
      &[data-is-expanded='true'] {
        height: var(--height-section-mobile);

        ${FiltersSubSection} {
          opacity: 1;
          transition: opacity var(--tr-base) 0.15s;
        }

        &:after {
          opacity: 1;
        }
      }

      &[data-is-expanded='false'] {
        height: 0px;
      }
    }
  }
`;